import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { apiGet, apiPost, fmtDate, statusLabel } from '../utils/api';

const WASTE_CATEGORIES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
const API = process.env.REACT_APP_API_URL;

export function AdminProfiles({ filterExpiring }) {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const location = useLocation();
  const initialStatus = new URLSearchParams(location.search).get('status') || '';
  const [status, setStatus]     = useState(initialStatus);
  const navigate = useNavigate();

  useEffect(() => { load(); }, [filterExpiring, initialStatus]);

  function load() {
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (search) params.set('search', search);
    if (filterExpiring) params.set('expiring', 'true');
    apiGet(`/profiles?${params}`).then(setProfiles).catch(console.error).finally(() => setLoading(false));
  }

  function handleSearch(e) {
    e.preventDefault();
    load();
  }

  return (
    <div>
       <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>{filterExpiring ? 'Expiring Profiles' : 'All Profiles'}</h1>
          <p>{filterExpiring ? 'Profiles expiring within 90 days' : 'Manage all waste profile submissions'}</p>
        </div>
        {!filterExpiring && (
          <button className="btn btn-primary" onClick={() => navigate('/admin/profiles/new')}>
            + New Profile
          </button>
        )}
      </div>

      {!filterExpiring && (
        <form onSubmit={handleSearch} style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input
              placeholder="Search by name, company, profile #, or email…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ flex: 1, minWidth: 0 }}
              autoComplete="off"
            />
            <button type="submit" className="btn btn-primary" style={{ flexShrink: 0 }}>Search</button>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <select value={status} onChange={e => { setStatus(e.target.value); }} style={{ width: 220 }}>
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="revisions_requested">Revisions Requested</option>
              <option value="expired">Expired</option>
            </select>
          </div>
        </form>
      )}

      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div className="loading"><div className="spinner" /></div>
        ) : profiles.length === 0 ? (
          <div className="empty-state"><div className="icon">📋</div><p>No profiles found.</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Profile #</th>
                <th>Waste Name</th>
                <th>Generator Name</th>
                <th>Transporter / Customer</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>Expires</th>
                <th></th>
              </tr></thead>
              <tbody>
                {profiles.map(p => (
                  <tr key={p.id}>
                    <td><code style={{ fontSize: 11 }}>{p.profile_number || '—'}</code></td>
                    <td><strong>{p.waste_name}</strong></td>
                    <td>{p.company_name}</td>
                    <td className="text-muted">{p.vendor_company}</td>
                    <td><span className={`badge badge-${p.status}`}>{statusLabel(p.status)}</span></td>
                    <td className="text-muted">{fmtDate(p.submitted_at)}</td>
                    <td className="text-muted" style={{ color: p.status === 'approved' && new Date(p.expires_at) < new Date(Date.now() + 30*86400000) ? '#c62828' : undefined }}>
                      {fmtDate(p.expires_at)}
                    </td>
                    <td>
                      <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/admin/profiles/${p.id}`)}>
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ padding: '8px 14px', fontSize: 12, color: 'var(--ink-lt)', borderTop: '1px solid var(--border)' }}>
              {profiles.length} profile{profiles.length !== 1 ? 's' : ''}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function AdminProfileDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile]         = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [modal, setModal]             = useState(null);
  const [reason, setReason]           = useState('');
  const [notes, setNotes]             = useState('');
  const [categories, setCategories]   = useState([]);
  const [saving, setSaving]           = useState(false);
  const [msg, setMsg]                 = useState('');
  const [error, setError]             = useState('');
  const [pdfLoading, setPdfLoading]   = useState(false);
  const [zipLoading, setZipLoading]   = useState(false);

  function toggleCategory(cat) {
    setCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  }

  // View attachment inline in new tab
  const openAttachment = async (attId) => {
    const token = localStorage.getItem('awwt_token');
    const res = await fetch(`${API}/api/attachments/${attId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) return alert('Could not load file.');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  // Force-download a single attachment
  const downloadAttachment = async (attId, filename) => {
    const token = localStorage.getItem('awwt_token');
    const res = await fetch(`${API}/api/attachments/${attId}?download=1`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) return alert('Could not download file.');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename || 'attachment';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  // Download all attachments as ZIP
  const downloadAllZip = async () => {
    setZipLoading(true);
    try {
      const token = localStorage.getItem('awwt_token');
      const res = await fetch(`${API}/api/attachments/profile/${id}/zip`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { alert('Could not create zip archive.'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attachments-${profile?.profile_number || id}.zip`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      alert('Failed to download zip.');
    } finally {
      setZipLoading(false);
    }
  };

  // Download full profile as PDF
  const downloadPDF = async () => {
    setPdfLoading(true);
    try {
      const token = localStorage.getItem('awwt_token');
      const res = await fetch(`${API}/api/profiles/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { alert('Could not generate PDF.'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `waste-profile-${profile?.profile_number || id}.pdf`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      alert('Failed to download PDF.');
    } finally {
      setPdfLoading(false);
    }
  };

  // Delete draft profile
  const handleDeleteDraft = async () => {
    setSaving(true); setError('');
    try {
      const token = localStorage.getItem('awwt_token');
      const res = await fetch(`${API}/api/profiles/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Failed to delete profile.');
        return;
      }
      navigate('/admin/profiles');
    } catch (err) {
      setError('Failed to delete profile.');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      apiGet(`/profiles/${id}`),
      apiGet(`/attachments/profile/${id}`)
    ]).then(([p, atts]) => {
      setProfile(p);
      setAttachments(atts || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, [id]);

  async function handleApprove() {
    setSaving(true); setError('');
    try {
      await apiPost(`/profiles/${id}/approve`, { waste_category: categories.join(', '), admin_notes: notes });
      setMsg('Profile approved successfully.');
      setModal(null);
      setCategories([]);
      setProfile(p => ({ ...p, status: 'approved' }));
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  async function handleReject() {
    if (!reason) { setError('Please provide a rejection reason.'); return; }
    setSaving(true); setError('');
    try {
      await apiPost(`/profiles/${id}/reject`, { rejection_reason: reason });
      setMsg('Profile rejected.');
      setModal(null);
      setProfile(p => ({ ...p, status: 'rejected' }));
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  async function handleRevisions() {
    if (!notes) { setError('Please provide revision notes.'); return; }
    setSaving(true); setError('');
    try {
      await apiPost(`/profiles/${id}/request-revisions`, { revision_notes: notes });
      setMsg('Revisions requested.');
      setModal(null);
      setProfile(p => ({ ...p, status: 'revisions_requested' }));
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!profile) return <div className="empty-state"><p>Profile not found.</p></div>;

  const canAct = ['submitted', 'under_review'].includes(profile.status);
  const isDraft = profile.status === 'draft';

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/admin/profiles')} style={{ marginBottom: 8 }}>
            ← Back to Profiles
          </button>
          <h1>{profile.waste_name}</h1>
          <p>{profile.profile_number || 'No profile number assigned'} · <span className={`badge badge-${profile.status}`}>{statusLabel(profile.status)}</span></p>
        </div>
        <div className="btn-group" style={{ flexWrap: 'wrap', justifyContent: 'flex-end', gap: 8 }}>
          {/* Download PDF — always available */}
          <button
            className="btn btn-danger btn-sm"
            onClick={downloadPDF}
            disabled={pdfLoading}
            title="Download full profile as PDF"
          >
            {pdfLoading ? '⏳ Generating…' : '⬇ Download Profile PDF'}
          </button>

          {/* Delete Draft — only when status is draft */}
          {isDraft && (
            <button
              className="btn btn-secondary btn-sm"
              style={{ color: '#c62828', borderColor: '#c62828' }}
              onClick={() => { setModal('delete'); setError(''); }}
            >
              🗑 Delete Draft
            </button>
          )}

          {/* Approve / Revisions / Reject — only when submitted/under review */}
          {canAct && (
            <>
              <button className="btn btn-success" onClick={() => { setModal('approve'); setError(''); setNotes(''); setCategories([]); }}>
                ✓ Approve
              </button>
              <button className="btn btn-warn" onClick={() => { setModal('revisions'); setError(''); setNotes(''); }}>
                ↩ Request Revisions
              </button>
              <button className="btn btn-danger" onClick={() => { setModal('reject'); setError(''); setReason(''); }}>
                ✕ Reject
              </button>
            </>
          )}
        </div>
      </div>

      {msg && <div className="alert alert-success" style={{ marginBottom: 16 }}>{msg}</div>}

      <div className="card">
        {/* Generator Info */}
        <div className="form-section-title">Generator / Site Information</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 0', marginBottom: 20 }}>
          {[
            ['Vendor - Transporter / Customer', profile.transporter_customer || profile.vendor_company],
            ['Generator Name', profile.company_name],
            ['EPA ID', profile.epa_id],
            ['Contact', profile.contact_name],
            ['Title', profile.contact_title],
            ['Email', profile.contact_email],
            ['Phone', profile.contact_phone],
            ['Generator Site Address', [profile.generator_address, profile.generator_city, profile.generator_state, profile.generator_zip].filter(Boolean).join(', ')],
          ].map(([k, v]) => (
            <div key={k} className="detail-row">
              <div className="detail-label">{k}</div>
              <div className="detail-value">{v || '—'}</div>
            </div>
          ))}
        </div>

        {/* Waste Characterization */}
        <div className="form-section-title">Waste Characterization</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 0', marginBottom: 20 }}>
          {[
            ['Waste Name', profile.waste_name],
            ['Physical State', profile.physical_state],
            ['Color', profile.color],
            ['Odor', profile.odor],
            ['pH Range', profile.ph_min || profile.ph_max ? `${profile.ph_min ?? '?'} – ${profile.ph_max ?? '?'}` : null],
            ['Viscosity', profile.viscosity],
          ].map(([k, v]) => (
            <div key={k} className="detail-row">
              <div className="detail-label">{k}</div>
              <div className="detail-value">{v || '—'}</div>
            </div>
          ))}
        </div>

        {/* Waste Sources */}
        {profile.waste_sources && profile.waste_sources.length > 0 && (
          <>
            <div className="form-section-title">Waste Sources</div>
            <div style={{ marginBottom: 20 }}>
              {profile.waste_sources.map(s => (
                <span key={s} style={{ display: 'inline-block', background: 'var(--cream-dk)', borderRadius: 4, padding: '3px 10px', marginRight: 6, marginBottom: 6, fontSize: 13 }}>{s}</span>
              ))}
            </div>
          </>
        )}

        {/* DOT Info */}
        <div className="form-section-title">DOT / Regulatory</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 0', marginBottom: 20 }}>
          {[
            ['Shipping Name', profile.dot_shipping_name],
            ['UN Number', profile.dot_un_number],
            ['Hazard Class', profile.dot_hazard_class],
            ['Packing Group', profile.dot_packing_group],
          ].map(([k, v]) => (
            <div key={k} className="detail-row">
              <div className="detail-label">{k}</div>
              <div className="detail-value">{v || '—'}</div>
            </div>
          ))}
        </div>

        {/* Handling */}
        <div className="form-section-title">Handling & Safety</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 0', marginBottom: 20 }}>
          {[
            ['Special Handling', profile.special_handling],
            ['Incompatible Materials', profile.incompatible_materials],
            ['PPE Required', Array.isArray(profile.ppe_required) ? profile.ppe_required.join(', ') : profile.ppe_required],
            ['Emergency Contact', profile.emergency_contact],
            ['Emergency Phone', profile.emergency_phone],
          ].map(([k, v]) => (
            <div key={k} className="detail-row">
              <div className="detail-label">{k}</div>
              <div className="detail-value">{v || '—'}</div>
            </div>
          ))}
        </div>

        {/* Certification */}
        <div className="form-section-title">Certification</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 0', marginBottom: 20 }}>
          {[
            ['Certified By', profile.certifier_name],
            ['Title', profile.certifier_title],
            ['Certified At', fmtDate(profile.certified_at)],
            ['Submitted At', fmtDate(profile.submitted_at)],
            ['Approved At', fmtDate(profile.approved_at)],
            ['Expires At', fmtDate(profile.expires_at)],
          ].map(([k, v]) => (
            <div key={k} className="detail-row">
              <div className="detail-label">{k}</div>
              <div className="detail-value">{v || '—'}</div>
            </div>
          ))}
        </div>

        {/* Admin Notes */}
        {profile.admin_notes && (
          <>
            <div className="form-section-title">Admin Notes</div>
            <div style={{ background: 'var(--cream)', padding: 12, borderRadius: 'var(--radius)', marginBottom: 20, fontSize: 13 }}>
              {profile.admin_notes}
            </div>
          </>
        )}

        {profile.rejection_reason && (
          <>
            <div className="form-section-title" style={{ color: '#c62828' }}>Rejection Reason</div>
            <div style={{ background: '#ffebee', padding: 12, borderRadius: 'var(--radius)', marginBottom: 20, fontSize: 13, color: '#c62828' }}>
              {profile.rejection_reason}
            </div>
          </>
        )}

        {profile.revision_notes && (
          <>
            <div className="form-section-title" style={{ color: '#e65100' }}>Revision Notes</div>
            <div style={{ background: '#fff3e0', padding: 12, borderRadius: 'var(--radius)', marginBottom: 20, fontSize: 13, color: '#e65100' }}>
              {profile.revision_notes}
            </div>
          </>
        )}

        {/* ── Attachments ─────────────────────────────────────────── */}
        <div className="form-section-title">Attachments / Lab Reports</div>
        {attachments.length === 0 ? (
          <p className="text-muted" style={{ marginBottom: 20 }}>No attachments uploaded for this profile.</p>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
              {attachments.map(att => (
                <div key={att.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 14px', background: 'var(--cream)', borderRadius: 'var(--radius)',
                  border: '1px solid var(--border)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 20 }}>📎</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{att.original_name}</div>
                      <div style={{ fontSize: 11, color: 'var(--ink-lt)' }}>
                        {att.mime_type} · {Math.round(att.file_size / 1024)} KB · {fmtDate(att.created_at)}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      onClick={() => openAttachment(att.id)}
                      className="btn btn-secondary btn-sm"
                      title="Open in new tab"
                    >
                      View
                    </button>
                    <button
                      onClick={() => downloadAttachment(att.id, att.original_name)}
                      className="btn btn-secondary btn-sm"
                      title="Download file"
                    >
                      ⬇ Download
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Download All ZIP bar */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 14px', background: '#f0f4f0', borderRadius: 'var(--radius)',
              border: '1px solid #c8dcc8', marginBottom: 20
            }}>
              <div style={{ fontSize: 13, color: 'var(--ink-mid)' }}>
                <strong>{attachments.length}</strong> file{attachments.length !== 1 ? 's' : ''} attached
              </div>
              <button
                onClick={downloadAllZip}
                disabled={zipLoading}
                className="btn btn-secondary btn-sm"
                style={{ borderColor: 'var(--forest, #2d6a4f)', color: 'var(--forest, #2d6a4f)' }}
              >
                {zipLoading ? '⏳ Zipping…' : '⬇ Download All (.zip)'}
              </button>
            </div>
          </>
        )}
      </div>

      {/* ── Approve Modal ──────────────────────────────────────────── */}
      {modal === 'approve' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Approve Profile</h2>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label>Waste Category</label>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
                {WASTE_CATEGORIES.map(cat => (
                  <label
                    key={cat}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      cursor: 'pointer', padding: '6px 14px',
                      border: `1.5px solid ${categories.includes(cat) ? 'var(--forest, #2d6a4f)' : 'var(--border)'}`,
                      borderRadius: 6,
                      background: categories.includes(cat) ? 'var(--forest-lt, #eaf4ee)' : '#fff',
                      color: categories.includes(cat) ? 'var(--forest, #2d6a4f)' : '#333',
                      fontSize: 15, fontWeight: 600,
                      transition: 'all .15s',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={categories.includes(cat)}
                      onChange={() => toggleCategory(cat)}
                      style={{ width: 15, height: 15, accentColor: 'var(--forest, #2d6a4f)', cursor: 'pointer' }}
                    />
                    {cat}
                  </label>
                ))}
              </div>
              {categories.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ink-mid)' }}>
                  Selected: {categories.join(', ')}
                </div>
              )}
            </div>
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label>Admin Notes (optional)</label>
              <textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Internal notes about this approval…" />
            </div>
            <div className="btn-group">
              <button className="btn btn-success" onClick={handleApprove} disabled={saving}>
                {saving ? 'Approving…' : 'Confirm Approval'}
              </button>
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reject Modal ───────────────────────────────────────────── */}
      {modal === 'reject' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Reject Profile</h2>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label>Rejection Reason <span className="req">*</span></label>
              <textarea rows={4} value={reason} onChange={e => setReason(e.target.value)} placeholder="Explain why this profile is being rejected…" />
            </div>
            <div className="btn-group">
              <button className="btn btn-danger" onClick={handleReject} disabled={saving}>
                {saving ? 'Rejecting…' : 'Confirm Rejection'}
              </button>
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Revisions Modal ────────────────────────────────────────── */}
      {modal === 'revisions' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Request Revisions</h2>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label>Revision Notes <span className="req">*</span></label>
              <textarea rows={4} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Describe what needs to be changed or added…" />
            </div>
            <div className="btn-group">
              <button className="btn btn-warn" onClick={handleRevisions} disabled={saving}>
                {saving ? 'Sending…' : 'Send Revision Request'}
              </button>
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Draft Modal ─────────────────────────────────────── */}
      {modal === 'delete' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Delete Draft Profile</h2>
            {error && <div className="alert alert-error">{error}</div>}
            <p style={{ marginBottom: 20, color: '#555' }}>
              Are you sure you want to permanently delete this draft profile?
              <br />
              <strong style={{ color: '#c62828' }}>This cannot be undone.</strong> All attachments will also be removed.
            </p>
            <div style={{ background: '#f9f9f7', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 20 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{profile.waste_name}</div>
              <div style={{ fontSize: 12, color: 'var(--ink-lt)' }}>{profile.company_name}</div>
            </div>
            <div className="btn-group">
              <button
                className="btn btn-danger"
                onClick={handleDeleteDraft}
                disabled={saving}
              >
                {saving ? 'Deleting…' : 'Yes, Delete Draft'}
              </button>
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
